import { Box, CircularProgress, Container, InputBase, Typography, styled, useTheme } from "@mui/material"
import SearchIcon from "@mui/icons-material/Search"
import React, { FormEvent, useEffect, useRef, useState } from "react"
import { useVideoAPI } from "../services/api/hooks/useVideoAPI"
import { VideoQueryType } from "../services/api/video"
import { useDirectAPI } from "../services/api/hooks/useDirectAPI"
import { DirectQueryType } from "../services/api/direct"
import { StylelessLink, delay } from "./Util"
import { useLocation, useNavigate, useSearchParams } from "react-router-dom"

const Search = styled("div")(({ theme }) => ({
  position: "relative",
  borderRadius: theme.name === "classic" ? "100px" : theme.shape.borderRadius,
  backgroundColor: theme.palette.element.searchBar.background,
  "&:hover": {
    backgroundColor: theme.palette.element.searchBar.backgroundHover,
  },
  marginRight: theme.name === "classic" ? 0 : theme.spacing(2),
  marginLeft: theme.name === "classic" ? 0 : theme.spacing(2),
  width: "100%",
  overflow: "hidden",
  [theme.breakpoints.up("sm")]: {
    marginLeft: theme.spacing(3),
    width: "auto",
  },
}))

const SearchIconWrapper = styled("div")(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: "100%",
  position: "absolute",
  right: theme.name === "classic" ? 0 : undefined,
  left: theme.name === "classic" ? undefined : 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: theme.name === "classic" ? "-5px 0 5px #0006" : undefined,
  zIndex: 1,
}))

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: theme.name === "classic" ? "black" : "inherit",
  width: "100%",
  "& .MuiInputBase-input": {
    padding: theme.spacing(1, 1, 1, 1),
    paddingLeft: theme.name === "classic" ? theme.spacing(2) : `calc(1em + ${theme.spacing(4)})`,
    paddingRight: theme.name === "classic" ? `calc(1em + ${theme.spacing(4)})` : theme.spacing(2),
    width: "100%",
  },
}))

interface SearchBarProps {
  setSearchFocus?: (focused: boolean) => void
}

export const SearchBar = ({ setSearchFocus }: SearchBarProps): React.ReactElement => {
  const [search, setSearch] = useState<string>("")
  const [isFocused, setIsFocused] = useState<boolean>(false)
  const navigate = useNavigate()
  const location = useLocation()

  const [searchParams] = useSearchParams()
  const querySearch = searchParams.get("q") ?? ""

  const { videosData: videoResults, loading: loadingVideoResults } = useVideoAPI({
    constraint: VideoQueryType.SEARCH,
    search: search,
  })
  const { directsData: directResults, loading: loadingDirectsResults } = useDirectAPI({
    constraint: DirectQueryType.SEARCH,
    search: search,
  })
  const focusContainerRef = useRef<HTMLDivElement | null>(null)
  const theme = useTheme()

  useEffect(() => {
    setIsFocused(false)
    setSearchFocus?.(false)
  }, [location])

  useEffect(() => {
    setSearch(querySearch)
  }, [querySearch])

  const handleFocus = () => {
    setIsFocused(true)
    setSearchFocus?.(true)
  }

  const handleBlur = async () => {
    await delay(100)
    if (focusContainerRef.current && focusContainerRef.current.contains(document.activeElement)) {
      return
    }
    setIsFocused(false)
    setSearchFocus?.(false)
  }

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    const newPath = search.trim() ? `/videos?q=${search.trim()}` : "/videos"

    if (window.location.pathname.startsWith("/videos")) {
      navigate(newPath, { replace: true })
    } else {
      navigate(newPath)
    }
  }

  return (
    <Container maxWidth="md" ref={focusContainerRef} onFocus={handleFocus} onBlur={handleBlur}>
      <form onSubmit={handleSubmit}>
        <Search>
          <SearchIconWrapper sx={{ cursor: "pointer" }} onClick={handleSubmit}>
            {loadingVideoResults || loadingDirectsResults ? (
              <CircularProgress size={24} sx={{ color: theme.palette.element.searchBar.icon }} />
            ) : (
              <SearchIcon sx={{ color: theme.palette.element.searchBar.icon }} />
            )}
          </SearchIconWrapper>
          <StyledInputBase
            placeholder={theme.name === "classic" ? "Rechercher une vidéo, un direct..." : "Rechercher…"}
            inputProps={{ "aria-label": "search" }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Search>
      </form>
      {isFocused && (
        <Box
          sx={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            marginLeft: "auto",
            marginRight: "auto",
            width: "50%",
            minWidth: "300px",
            backdropFilter: theme.name === "glass" ? "blur(5px)" : "unset",
          }}
        >
          {!loadingVideoResults && !loadingDirectsResults && (
            <>
              {videoResults.length > 0 && (
                <>
                  <Typography
                    sx={{
                      padding: 1,
                      backgroundColor:
                        theme.palette.element.searchBar.results?.background ?? theme.palette.primary.main,
                    }}
                    fontWeight="bold"
                    textAlign="start"
                  >
                    {"Vidéos :"}
                  </Typography>
                  <Box>
                    {videoResults.slice(0, 10).map((video) => (
                      <StylelessLink key={video.id} to={`/video?v=${video.id}`}>
                        <Box
                          sx={{
                            backgroundColor:
                              theme.palette.element.searchBar.results?.background ?? theme.palette.primary.main,
                            "&:hover": {
                              backgroundColor:
                                theme.palette.element.searchBar.results?.hoverBackground ?? theme.palette.primary.light,
                            },
                            transition: theme.name === "classic" ? undefined : ".2s",
                          }}
                        >
                          <Typography sx={{ padding: 1, pl: 2 }} textAlign="start" textOverflow="ellipsis" noWrap>
                            {video.title}
                          </Typography>
                        </Box>
                      </StylelessLink>
                    ))}
                  </Box>
                </>
              )}
              {directResults.length > 0 && (
                <>
                  <Typography
                    sx={{
                      padding: 1,
                      backgroundColor:
                        theme.palette.element.searchBar.results?.background ?? theme.palette.primary.main,
                    }}
                    fontWeight="bold"
                    textAlign="start"
                  >
                    {"Directs :"}
                  </Typography>
                  <Box>
                    {directResults.slice(0, 5).map((direct) => (
                      <StylelessLink key={direct.id} to={`/direct?d=${direct.id}`}>
                        <Box
                          sx={{
                            backgroundColor:
                              theme.palette.element.searchBar.results?.background ?? theme.palette.primary.main,
                            "&:hover": {
                              backgroundColor:
                                theme.palette.element.searchBar.results?.hoverBackground ?? theme.palette.primary.light,
                            },
                            transition: theme.name === "classic" ? undefined : ".2s",
                          }}
                        >
                          <Typography sx={{ padding: 1, pl: 2 }} textAlign="start" textOverflow="ellipsis" noWrap>
                            {direct.title}
                          </Typography>
                        </Box>
                      </StylelessLink>
                    ))}
                  </Box>
                </>
              )}
            </>
          )}
        </Box>
      )}
    </Container>
  )
}
